/**
 * Markdown Utility Functions
 *
 * Utilities for extracting and manipulating markdown content,
 * particularly for preserving table formatting when saving selections.
 */

/**
 * Extract all markdown tables from a markdown string
 * @param markdown The full markdown content
 * @returns Array of table markdown strings
 */
export function extractTablesFromMarkdown(markdown: string): string[] {
  const tables: string[] = [];
  const lines = markdown.split('\n');
  let currentTable: string[] = [];
  let inTable = false;

  for (const line of lines) {
    // A table line starts with | or contains | as cell separators
    // Also check for separator lines like |---|---|
    const isTableLine = line.trim().startsWith('|') ||
      (line.includes('|') && /^\s*\|/.test(line));
    const isSeparatorLine = /^\s*\|[-:\s|]+\|\s*$/.test(line);

    if (isTableLine || isSeparatorLine) {
      if (!inTable) {
        inTable = true;
        currentTable = [];
      }
      currentTable.push(line);
    } else if (inTable) {
      // End of table - save it if it has enough rows (header + separator + at least 1 data row)
      if (currentTable.length >= 2) {
        tables.push(currentTable.join('\n'));
      }
      currentTable = [];
      inTable = false;
    }
  }

  // Don't forget table at end of content
  if (inTable && currentTable.length >= 2) {
    tables.push(currentTable.join('\n'));
  }

  return tables;
}

/**
 * Find the first markdown table that might correspond to a text selection
 * @param markdown The full markdown content
 * @param selectedText The plain text that was selected
 * @returns The full table markdown if found, or null
 */
export function findTableForSelection(markdown: string, selectedText: string): string | null {
  const tables = extractTablesFromMarkdown(markdown);

  if (tables.length === 0) return null;

  // Normalize text for comparison (remove extra whitespace)
  const normalizeText = (text: string) =>
    text.replace(/\s+/g, ' ').trim().toLowerCase();

  const normalizedSelection = normalizeText(selectedText);

  // Find the table that contains the selected text
  for (const table of tables) {
    // Extract plain text from table for comparison
    const tablePlainText = table
      .split('\n')
      .map(line => line.replace(/\|/g, ' ').replace(/-+/g, '').trim())
      .join(' ');

    const normalizedTable = normalizeText(tablePlainText);

    // Check if the selection text appears in this table
    if (normalizedTable.includes(normalizedSelection) ||
        normalizedSelection.includes(normalizedTable.substring(0, Math.min(50, normalizedTable.length)))) {
      return table;
    }
  }

  // If no exact match, return the first table if selection contains table-like content
  // (multiple columns of data)
  if (tables.length === 1 && selectedText.includes('\t')) {
    return tables[0];
  }

  return null;
}

/**
 * Check if a selection appears to be from a table
 * @param selectedText The plain text selection
 * @returns true if the selection looks like table content
 */
export function isTableSelection(selectedText: string): boolean {
  // Table selections often have:
  // - Tab characters (from cell separation)
  // - Multiple lines with similar structure
  // - Repeating patterns of whitespace

  const lines = selectedText.split('\n').filter(l => l.trim());

  // If only one line, probably not a table
  if (lines.length < 2) return false;

  // Check if lines have consistent column-like structure
  // (similar number of "words" per line)
  const wordCounts = lines.map(l => l.split(/\s{2,}|\t/).length);
  const avgWords = wordCounts.reduce((a, b) => a + b, 0) / wordCounts.length;

  // If most lines have 2+ columns and similar structure, likely a table
  return avgWords >= 2 && wordCounts.every(c => c >= 2);
}

/**
 * Check if the selection range contains or intersects with a table element
 * @param range The selection range
 * @returns The table element if found, null otherwise
 */
export function findTableInSelection(range: Range): HTMLTableElement | null {
  // First check if we're directly inside a table
  const container = range.commonAncestorContainer;
  const directTable = container instanceof Element
    ? container.closest('table')
    : container.parentElement?.closest('table');

  if (directTable) return directTable as HTMLTableElement;

  // Check if the selection contains a table (e.g., selecting heading + table)
  const containerElement = container instanceof Element
    ? container
    : container.parentElement;

  if (containerElement) {
    // Look for tables within the selection's common ancestor
    const tables = containerElement.querySelectorAll('table');
    if (tables.length > 0) {
      // Check if any table intersects with the selection
      for (const table of tables) {
        if (range.intersectsNode(table)) {
          return table as HTMLTableElement;
        }
      }
    }

    // Also check siblings - selection might span heading and table
    let sibling = containerElement.nextElementSibling;
    while (sibling) {
      if (sibling.tagName === 'TABLE' || sibling.querySelector('table')) {
        const table = sibling.tagName === 'TABLE' ? sibling : sibling.querySelector('table');
        if (table && range.intersectsNode(table)) {
          return table as HTMLTableElement;
        }
      }
      sibling = sibling.nextElementSibling;
    }
  }

  return null;
}

/**
 * Extract heading text from selected text (text before table content)
 * @param selectedText The full selected text
 * @param tableText The plain text of the table
 * @returns The heading portion if found
 */
export function extractHeadingFromSelection(selectedText: string, tableText: string): string {
  // Normalize for comparison
  const normalizedSelection = selectedText.replace(/\s+/g, ' ').trim();
  const normalizedTable = tableText.replace(/\s+/g, ' ').trim();

  // Find where the table content starts in the selection
  const tableStart = normalizedSelection.indexOf(normalizedTable.substring(0, 50));

  if (tableStart > 0) {
    // Extract text before the table
    const headingPortion = selectedText.substring(0, tableStart).trim();
    return headingPortion;
  }

  // Check first line as potential heading
  const lines = selectedText.split('\n');
  if (lines.length > 1 && lines[0].trim() && !lines[0].includes('\t')) {
    return lines[0].trim();
  }

  return '';
}

/**
 * Reconstruct table markdown from plain text selection
 * This is a fallback when we can't find the original table
 * @param selectedText The plain text selection from a table
 * @returns Reconstructed markdown table
 */
export function reconstructTableMarkdown(selectedText: string): string {
  const lines = selectedText.split('\n').filter(l => l.trim());

  if (lines.length < 1) return selectedText;

  // Split each line by multiple spaces or tabs
  const rows = lines.map(line =>
    line.split(/\s{2,}|\t/).map(cell => cell.trim()).filter(Boolean)
  );

  // Find max columns
  const maxCols = Math.max(...rows.map(r => r.length));

  // Pad rows to have consistent columns
  const paddedRows = rows.map(row => {
    while (row.length < maxCols) row.push('');
    return row;
  });

  // Build markdown table
  const mdLines: string[] = [];

  // Header row
  if (paddedRows.length > 0) {
    mdLines.push('| ' + paddedRows[0].join(' | ') + ' |');
    mdLines.push('| ' + paddedRows[0].map(() => '---').join(' | ') + ' |');
  }

  // Data rows
  for (let i = 1; i < paddedRows.length; i++) {
    mdLines.push('| ' + paddedRows[i].join(' | ') + ' |');
  }

  return mdLines.join('\n');
}
